import config from "../config";
import { LeadsFilter, Pagination, PaymentsFilter } from "../interfaces";
import request, { requestOptions } from "../utils/request";
const baseUrl = `${config.API_ROOT}/app/leads`;

const Leads = {
  async getLeadsTotals() {
    const options = await requestOptions("GET", {}, true);
    const url = new URL(`${baseUrl}-get-totals`);
    const { data } = await request(url.toString(), options);
    if (!data) {
      throw new Error("There was an error getting the leads totals");
    }
    const { total, escalatedTotal, actions } = data;
    return {
      totalCount: total || 0,
      escalatedTotal: escalatedTotal || 0,
      actions: actions || [],
    };
  },

  async getLeads({
    date = null,
    limit = null,
    cursor = null,
    direction = null,
    filters = null,
  }: {
    date?: null | string;
    limit?: null | number;
    cursor?: null | string;
    direction?: null | string;
    filters?: null | LeadsFilter;
  }) {
    const options = await requestOptions("GET", {}, true);
    const url = new URL(`${baseUrl}`);
    if (date) {
      url.searchParams.set("date", date);
    }
    if (limit) {
      url.searchParams.set("limit", String(limit));
    }

    if (direction) {
      url.searchParams.set("direction", direction);
    }
    if (cursor) {
      url.searchParams.set("cursor", String(cursor));
    }
    if (filters?.companies.length) {
      url.searchParams.set("companies", filters.companies.join(","));
    }
    if (filters?.actions.length) {
      filters?.actions.forEach((f) => {
        url.searchParams.set("actions", filters.actions.join(","));
      });
    }

    if (filters?.scores.length) {
      filters?.scores.forEach((f) => {
        url.searchParams.set("scores", filters.scores.join(","));
      });
    }

    if (filters?.statuses.length) {
      filters?.statuses.forEach((f) => {
        url.searchParams.set(f, String(true));
      });
    }

    const { data } = await request(url.toString(), options);

    const { contacts, nextCursor, previousCursor } = data;

    if (!data) {
      throw new Error("There was an error getting the contacts");
    }

    return {
      contacts: contacts || [],
      nextCursor: nextCursor || null,
      previousCursor: previousCursor || null,
    };
  },
  async getPaymentsTotals() {
    const options = await requestOptions("GET", {}, true);
    const url = new URL(`${baseUrl}/payments-get-totals`);
    const { data } = await request(url.toString(), options);
    if (!data) {
      throw new Error("There was an error getting the leads totals");
    }
    const { total, paidTotal } = data;
    return {
      totalCount: total || 0,
      paidTotal: paidTotal || 0,
    };
  },
  async getPayments({
    date = null,
    limit = null,
    page = null,
    cursor = null,
    direction = null,
    filters = null,
  }: {
    date?: null | string;
    limit?: null | number;
    page?: null | number;
    filters?: null | PaymentsFilter;
    cursor?: null | string;
    direction?: null | string;
  }) {
    const options = await requestOptions("GET", {}, true);
    const url = new URL(`${baseUrl}/payments`);
    if (date) {
      url.searchParams.set("date", date);
    }
    if (limit) {
      url.searchParams.set("limit", String(limit));
    }
    if (page) {
      url.searchParams.set("page", String(page));
    }
    if (filters?.companies.length) {
      url.searchParams.set("companies", filters.companies.join(","));
    }
    if (direction) {
      url.searchParams.set("direction", direction);
    }
    if (cursor) {
      url.searchParams.set("cursor", String(cursor));
    }

    if (filters?.statuses.length) {
      filters?.statuses.forEach((f) => {
        url.searchParams.set(f, String(true));
      });
    }

    const { data } = await request(url.toString(), options);

    const { payments, nextCursor, previousCursor } = data;

    if (!data) {
      throw new Error("There was an error getting the contacts");
    }

    return {
      payments: payments || [],
      nextCursor: nextCursor || null,
      previousCursor: previousCursor || null,
    };
  },
  async getExport() {
    const options = await requestOptions("GET", {}, true);
    const url = new URL(`${baseUrl}?csv=true`);

    const { data, err } = await request(url.toString(), options);

    if (err) {
      return {
        err,
      };
    }
    const { contacts, toolCalls } = data;

    return {
      contacts: contacts || [],
      toolCalls: toolCalls || [],
    };
  },
  async getPaymentsExport() {
    const options = await requestOptions("GET", {}, true);
    const url = new URL(`${baseUrl}/payments?csv=true`);

    const { data, err } = await request(url.toString(), options);

    if (err) {
      return {
        err,
      };
    }
    const { payments } = data;

    return {
      payments: payments || [],
    };
  },
  async postTransferFunds({ paymentId }: { paymentId: string }) {
    const options = await requestOptions("POST", { paymentId }, true);
    const url = new URL(`${baseUrl}/payments-transfer-funds`);
    return request(url.toString(), options);
  },
  async postHydrateBalance({ paymentId }: { paymentId: string }) {
    const options = await requestOptions("POST", { paymentId }, true);
    const url = new URL(`${baseUrl}/payments-hydrate-balance`);
    return request(url.toString(), options);
  },

  async postRefundFunds({
    paymentId,
    amount,
  }: {
    paymentId: string;
    amount: number;
  }) {
    const options = await requestOptions("POST", { paymentId, amount }, true);
    const url = new URL(`${baseUrl}/payments-refund-funds`);
    return request(url.toString(), options);
  },
  async postReconcilePayment({
    paymentId,
    reconciled,
  }: {
    paymentId: string;
    reconciled: boolean;
  }) {
    const options = await requestOptions(
      "POST",
      { paymentId, reconciled },
      true,
    );
    const url = new URL(`${baseUrl}/payments-reconcile`);
    return request(url.toString(), options);
  },
};

export default Leads;
